import { Injectable } from '@angular/core';
import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  Object3D,
  RepeatWrapping,
  ShapeGeometry,
  Texture,
  UVMapping,
  Vector3,
} from 'three';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import type { QmodelJsonObject } from '../models/qmodel/qmodel-json-object';
import type { QmodelJsonMaterial } from '../models/qmodel/qmodel-json-material';
import type { QmodelJsonText } from '../models/qmodel/qmodel-json-text';
import { globalV3dSettings } from '../data/visualize-3d-global-settings';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createQmodelMaterialKey } from '../models/qmodel/qmodel-json-material';

// A lot of this code was adapted and improved from qmdTjs.js provided by Q-soft
// Pretty much just the math is the same!
// Source: https://source.magicad.com/Progman/projects/magiqpdservice/repositories/subversion/MagiQpdService/QpdSrv/ThreeJsUtils
@Injectable({
  providedIn: 'root',
})
export class QmdJsonToThreejsConverterService {
  font: Font | undefined;

  materialCache: Record<string, Material> = {};

  constructor() {
    this.initFont();
  }

  /**
   * Initializes the font by trying to load the font resource.
   */
  initFont(): void {
    const fontLoader = new FontLoader();
    fontLoader.load(
      globalV3dSettings.qmodelFontUrl,
      font => {
        this.font = font;
      },
      undefined,
      () => {
        console.error('Failed to load font for qmodels', globalV3dSettings.qmodelFontUrl);
        this.font = undefined;
      },
    );
  }

  convertToObject3d(qmdObject: QmodelJsonObject): Object3D {
    const object = new Object3D();
    const materials = this.createMaterials(qmdObject);
    const meshes = this.createMeshes(qmdObject, materials);
    const lines = this.createLines(qmdObject, materials);
    const texts = this.createTexts(qmdObject);
    object.name = qmdObject.name;

    // Object3D.add throws if given an empty array.
    if (meshes.length > 0) {
      object.add(...meshes);
    }
    if (lines.length > 0) {
      object.add(...lines);
    }
    if (texts.length > 0) {
      object.add(...texts);
    }

    return object;
  }

  private createMaterials(qmdObject: QmodelJsonObject): Record<string, Material> {
    const materials: Record<string, Material> = {};
    if (!qmdObject.Materials) {
      return materials;
    }

    if (qmdObject.Materials) {
      for (const qmdMaterial of qmdObject.Materials) {
        let material: Material;
        if (qmdMaterial.lty) {
          material = this.createLineMaterial(qmdMaterial);
        } else {
          material = this.createSurfaceMaterial(qmdObject, qmdMaterial);
        }

        materials[qmdMaterial.id] = material;
      }
    }
    return materials;
  }

  private createLineMaterial(qmdMaterial: QmodelJsonMaterial): Material {
    const material = new LineBasicMaterial({
      name: qmdMaterial.id,
      color: qmdMaterial.clr || '#000000',
    });
    return material;
  }

  private createSurfaceMaterial(qmdObject: QmodelJsonObject, qmdMaterial: QmodelJsonMaterial): Material {
    const materialKey = createQmodelMaterialKey(qmdMaterial);

    // If the material already is created - just use the same one
    if (this.materialCache[materialKey]) {
      return this.materialCache[materialKey];
    }

    const clr = qmdMaterial.clr || '#000000';
    const shi = qmdMaterial.shi || 30;

    const material = new MeshPhongMaterial({
      name: qmdMaterial.id,
      color: clr,
      specular: clr,
      side: DoubleSide,
      shininess: shi,
    });

    if (qmdMaterial.tr) {
      material.transparent = true;
      material.opacity = 1 - qmdMaterial.tr * 0.01;
    }

    if (qmdMaterial.pic) {
      const texture = this.createTexture(qmdObject, qmdMaterial);
      material.map = texture;
    }

    this.materialCache[materialKey] = material;

    // Set up dispose handling: if the material is disposed, make sure to also remove it from the cache
    const onDispose = () => {
      this.materialCache[materialKey] = null;
      material.removeEventListener('dispose', onDispose);
    };
    material.addEventListener('dispose', onDispose);

    return material;
  }

  /**
   * Creates the texture from a base64 url.
   */
  private createTexture(qmdObject: QmodelJsonObject, qmdMaterial: QmodelJsonMaterial): Texture {
    // TODO: Add a texture cache so we reuse the same textures if multiple of the same qmodels.
    // However the base64 url needs to be the same in case of different parameters.
    const image = new Image();
    image.addEventListener('load', () => {
      texture.needsUpdate = true;
    });
    image.addEventListener('error', () => console.log(`failed to load texture for ${qmdObject.name} - ${qmdMaterial.id}`));
    image.src = qmdMaterial.pic;
    const texture = new Texture(image);
    texture.mapping = UVMapping;
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.repeat.set(1, 1);
    return texture;
  }

  private createMeshes(qmdObject: QmodelJsonObject, materials: Record<string, Material>): Mesh[] {
    const result: Mesh[] = [];
    if (!qmdObject.Meshes) {
      return result;
    }

    const geometryMappedPerMaterial = this.createGeometries(qmdObject, materials);
    for (const [materialId, geometry] of Object.entries(geometryMappedPerMaterial)) {
      const mesh = new Mesh(geometry, materials[materialId]);
      mesh.name = `mesh_${materialId}`;
      result.push(mesh);
    }

    return result;
  }

  private createGeometries(qmdObject: QmodelJsonObject, materials: Record<string, Material>): Record<string, BufferGeometry> {
    const geometriesPerMaterial: Record<string, BufferGeometry[]> = {};
    for (const mesh of qmdObject.Meshes) {
      // We exclude meshes that has no valid material (because qmdTjs does that).
      if (!materials[mesh.mtrId]) {
        continue;
      }

      const bufferGeometry = new BufferGeometry();
      bufferGeometry.setIndex(mesh.faces);
      bufferGeometry.setAttribute('position', new Float32BufferAttribute(mesh.pts, 3));

      if (mesh.uvs) {
        bufferGeometry.setAttribute('uv', new Float32BufferAttribute(mesh.uvs, 2));
      }
      bufferGeometry.computeVertexNormals();

      if (!geometriesPerMaterial[mesh.mtrId]) {
        geometriesPerMaterial[mesh.mtrId] = [];
      }
      geometriesPerMaterial[mesh.mtrId].push(bufferGeometry);
    }

    const result: Record<string, BufferGeometry> = {};
    for (const [key, geometries] of Object.entries(geometriesPerMaterial)) {
      const mergedBufferGeometry = mergeGeometries(geometries);
      mergedBufferGeometry.name = `geometry_${key}`;
      geometries.forEach(geometry => geometry.dispose());
      result[key] = mergedBufferGeometry;
    }

    return result;
  }

  private createLines(qmdObject: QmodelJsonObject, materials: Record<string, Material>): LineSegments[] {
    const result: LineSegments[] = [];
    if (!qmdObject.Lines) {
      return result;
    }
    const positionsPerMaterial: Record<string, number[]> = {};

    for (const qmdline of qmdObject.Lines) {
      if (!materials[qmdline.mtrId]) {
        continue;
      }

      const pts = qmdline.pts;
      const positions: number[] = [];
      const lastPointIndex = pts.length - 3;
      for (let i = 0; i < pts.length; i += 3) {
        positions.push(pts[i], pts[i + 1], pts[i + 2]);
        // We add the position again to create a link to the line.
        if (i !== 0 && i < lastPointIndex) {
          positions.push(pts[i], pts[i + 1], pts[i + 2]);
        }
      }

      if (!positionsPerMaterial[qmdline.mtrId]) {
        positionsPerMaterial[qmdline.mtrId] = [];
      }
      positionsPerMaterial[qmdline.mtrId].push(...positions);
    }

    for (const [key, positions] of Object.entries(positionsPerMaterial)) {
      const bufferGeometry = new BufferGeometry();
      bufferGeometry.name = `linegeometry_${key}`;
      bufferGeometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
      const mesh = new LineSegments(bufferGeometry, materials[key]);
      result.push(mesh);
    }

    return result;
  }

  private createTexts(qmdObject: QmodelJsonObject): Mesh[] {
    const result: Mesh[] = [];
    if (!qmdObject.Texts || !qmdObject.Texts.length) {
      return result;
    }

    if (!this.font) {
      console.warn(`can't generate texts for ${qmdObject.name} because there is no font.`);
      return result;
    }

    const materials = this.createTextMaterials(qmdObject.Texts);
    const geometriesPerMaterial: Record<string, BufferGeometry[]> = {};

    for (const textItem of qmdObject.Texts) {
      if (!textItem.txt) {
        continue;
      }

      if (!textItem.ag || textItem.ag.length < 2) {
        textItem.ag = 'LA';
      }

      this.createTextGeometry(textItem, materials, geometriesPerMaterial);

      const backVx = [-textItem.vx[0], -textItem.vx[1], -textItem.vx[2]];
      let backAlignment = textItem.ag;
      if (backAlignment[0] === 'L') {
        backAlignment = 'R' + backAlignment[1];
      } else if (backAlignment[0] === 'R') {
        backAlignment = 'L' + backAlignment[1];
      }
      const backItem: QmodelJsonText = {
        ...textItem,
        vx: backVx,
        ag: backAlignment,
      };
      this.createTextGeometry(backItem, materials, geometriesPerMaterial);
    }

    for (const [key, geometries] of Object.entries(geometriesPerMaterial)) {
      const mergedBufferGeometry = mergeGeometries(geometries);
      mergedBufferGeometry.name = `textgeometry_${key}`;
      geometries.forEach(geometry => geometry.dispose());
      const mesh = new Mesh(mergedBufferGeometry, materials[key]);
      result.push(mesh);
    }

    return result;
  }

  /**
   * Returns materials based on the text color.
   */
  private createTextMaterials(qmdTexts: QmodelJsonText[]): Record<string, Material> {
    const materials: Record<string, Material> = {};

    for (const text of qmdTexts) {
      const color = text.clr || '#000000';
      if (materials[color]) {
        continue;
      }
      const material = new MeshBasicMaterial({
        color,
      });
      materials[color] = material;
    }

    return materials;
  }

  private createTextGeometry(
    textItem: QmodelJsonText,
    materials: Record<string, Material>,
    geometriesPerMaterial: Record<string, BufferGeometry[]>,
  ): void {
    const shapes = this.font.generateShapes(textItem.txt, textItem.h);
    const geometry = new ShapeGeometry(shapes, globalV3dSettings.qmodelFontSegmentQuality);
    const color = textItem.clr || '#000000';

    const matrix = this.calculateTextMatrix(textItem, geometry);
    geometry.applyMatrix4(matrix);
    if (!geometriesPerMaterial[color]) {
      geometriesPerMaterial[color] = [];
    }
    geometriesPerMaterial[color].push(geometry);
  }

  private calculateTextMatrix(textItem: QmodelJsonText, geometry: ShapeGeometry): Matrix4 {
    geometry.computeBoundingBox();
    const axisX = new Vector3(textItem.vx[0], textItem.vx[1], textItem.vx[2]);
    const axisY = new Vector3(textItem.vy[0], textItem.vy[1], textItem.vy[2]);
    const axisZ = new Vector3();
    axisZ.crossVectors(axisX, axisY);
    axisX.normalize();
    axisY.normalize();
    axisZ.normalize();

    const position = this.calculateTextPosition(textItem, geometry, axisX, axisY);

    const matrix = new Matrix4();
    // This makes a matrix in the form of:
    // x.x, x.y, x.z, 0
    // y.x, y.y, y.z, 0
    // z.x, z.y, z.z, 0
    // 0,   0,   0,   1
    matrix.makeBasis(axisX, axisY, axisZ);
    matrix.setPosition(position);
    return matrix;
  }

  /**
   * Calculates the text position from the pt array and the "ag" value.
   */
  private calculateTextPosition(textItem: QmodelJsonText, geometry: BufferGeometry, axisX: Vector3, axisY: Vector3): Vector3 {
    const width = geometry.boundingBox.max.x - geometry.boundingBox.min.x;
    const height = geometry.boundingBox.max.y - geometry.boundingBox.min.y;

    const position = new Vector3(textItem.pt[0], textItem.pt[1], textItem.pt[2]);

    let deltaX = 0;
    let deltaY = 0;

    if (textItem.ag !== 'LA') {
      if (textItem.ag[0] === 'R') {
        deltaX = -width;
      } else if (textItem.ag[0] === 'C') {
        deltaX = -width / 2;
      }

      if (textItem.ag[1] === 'T') {
        deltaY = -height;
      } else if (textItem.ag[1] === 'M') {
        deltaY = -height / 2;
      }
    }

    if (textItem.ag[0] !== 'L') {
      position.x += axisX.x * deltaX;
      position.y += axisX.y * deltaX;
      position.z += axisX.z * deltaX;
    }

    if (textItem.ag[1] !== 'A') {
      position.x += axisY.x * deltaY;
      position.y += axisY.y * deltaY;
      position.z += axisY.z * deltaY;
    }

    return position;
  }
}
